/**
 * Customers Page - Professionelle Kundenverwaltung mit Material-UI
 * Optimiert für alle Geräte mit responsivem Design
 */

import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Alert,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Send as SendIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { Customer, Invoice, InvoiceStatus } from "../types/api-types";
import { apiClient } from "../services/apiClient";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import ConfirmDialog from "../components/Common/ConfirmDialog";

const CustomersPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [showForm, setShowForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(
    null
  );

  // Rechnungssuche States
  const [showInvoiceSearch, setShowInvoiceSearch] = useState(false);
  const [customerForInvoices, setCustomerForInvoices] =
    useState<Customer | null>(null);
  const [customerInvoices, setCustomerInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // Load customers
  const loadCustomers = async () => {
    try {
      setLoading(true);
      const customerList = await apiClient.getCustomers();
      console.log("Loaded customers:", customerList); // Debug log
      setCustomers(Array.isArray(customerList) ? customerList : []);
    } catch (err: any) {
      console.error("Error loading customers:", err); // Debug log
      setError(err.message || "Fehler beim Laden der Kunden");
      setCustomers([]); // Ensure customers is always an array
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  // Filter customers
  const filteredCustomers = (customers || []).filter(
    (customer) =>
      customer.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle delete
  const handleDeleteCustomer = async () => {
    if (!customerToDelete) return;

    try {
      await apiClient.deleteCustomer(customerToDelete.id);
      setCustomers(
        (customers || []).filter((c) => c.id !== customerToDelete.id)
      );
      setShowDeleteDialog(false);
      setCustomerToDelete(null);
    } catch (err: any) {
      setError(err.message || "Fehler beim Löschen des Kunden");
    }
  };

  // Load customer invoices
  const loadCustomerInvoices = async (customer: Customer) => {
    try {
      setLoadingInvoices(true);
      setCustomerForInvoices(customer);
      setShowInvoiceSearch(true);

      // Lade alle Rechnungen und filtere nach Kunde
      const allInvoices = await apiClient.getInvoices();
      console.log("Loaded invoices:", allInvoices); // Debug log

      // Stelle sicher, dass allInvoices ein Array ist
      const invoicesArray = Array.isArray(allInvoices) ? allInvoices : [];
      const filteredInvoices = invoicesArray.filter(
        (invoice) => invoice.customerId === customer.id
      );
      setCustomerInvoices(filteredInvoices);
    } catch (err: any) {
      console.error("Error loading customer invoices:", err);
      setError(err.message || "Fehler beim Laden der Kundenrechnungen");
      // Bei Fehler leeres Array setzen
      setCustomerInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  // Format date for display
  const formatDate = (date: Date | string) => {
    if (typeof date === "string") {
      return new Date(date).toLocaleDateString("de-DE");
    }
    return date.toLocaleDateString("de-DE");
  };

  // Format price
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(price);
  };

  // Handle edit
  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowForm(true);
  };

  // Handle add new
  const handleAddCustomer = () => {
    setSelectedCustomer(null);
    setShowForm(true);
  };

  // Get customer display name
  const getCustomerDisplayName = (customer: Customer) => {
    if (customer.firstName && customer.lastName) {
      return `${customer.firstName} ${customer.lastName}`;
    }
    return customer.email || "Unbekannter Kunde";
  };

  // Get customer initials for avatar
  const getCustomerInitials = (customer: Customer) => {
    if (customer.firstName && customer.lastName) {
      return `${customer.firstName.charAt(0)}${customer.lastName.charAt(
        0
      )}`.toUpperCase();
    }
    return customer.email?.charAt(0).toUpperCase() || "?";
  };

  // Get status chip for customer (based on activity/completeness)
  const getCustomerStatusChip = (customer: Customer) => {
    const hasCompleteInfo =
      customer.firstName && customer.lastName && customer.email;
    const hasContactInfo = customer.phone || customer.address;

    if (hasCompleteInfo && hasContactInfo) {
      return (
        <Chip
          label="Vollständig"
          color="success"
          size="small"
          icon={<PersonIcon sx={{ fontSize: 16 }} />}
          sx={{ fontWeight: "bold" }}
        />
      );
    } else if (hasCompleteInfo) {
      return (
        <Chip
          label="Grunddaten"
          color="warning"
          size="small"
          icon={<PersonIcon sx={{ fontSize: 16 }} />}
          sx={{ fontWeight: "bold" }}
        />
      );
    } else {
      return (
        <Chip
          label="Unvollständig"
          color="error"
          size="small"
          icon={<PersonIcon sx={{ fontSize: 16 }} />}
          sx={{ fontWeight: "bold" }}
        />
      );
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm("");
  };

  // Status display with enhanced styling
  const getStatusChip = (status: string) => {
    const statusConfig: Record<
      string,
      {
        label: string;
        color:
          | "default"
          | "primary"
          | "secondary"
          | "error"
          | "info"
          | "success"
          | "warning";
        icon?: React.ReactElement;
      }
    > = {
      [InvoiceStatus.DRAFT]: {
        label: "Entwurf",
        color: "info",
        icon: <EditIcon sx={{ fontSize: 16 }} />,
      },
      [InvoiceStatus.PAID]: {
        label: "Bezahlt",
        color: "success",
        icon: <CheckIcon sx={{ fontSize: 16 }} />,
      },
    };

    const config = statusConfig[status] || {
      label: status,
      color: "default" as const,
    };

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        {...(config.icon && { icon: config.icon })}
        sx={{
          fontWeight: "bold",
          borderRadius: 2,
          "& .MuiChip-icon": {
            marginLeft: "4px",
          },
        }}
      />
    );
  };

  if (loading) {
    return <LoadingSpinner text="Lade Kunden..." />;
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={loadCustomers}>
              Erneut versuchen
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (showForm) {
    return (
      <CustomerForm
        customer={selectedCustomer}
        onSave={async (customerData) => {
          try {
            if (selectedCustomer) {
              await apiClient.updateCustomer(selectedCustomer.id, customerData);
            } else {
              await apiClient.createCustomer(customerData);
            }
            await loadCustomers();
            setShowForm(false);
            setSelectedCustomer(null);
          } catch (err: any) {
            setError(err.message || "Fehler beim Speichern des Kunden");
          }
        }}
        onCancel={() => {
          setShowForm(false);
          setSelectedCustomer(null);
        }}
      />
    );
  }

  // Customer Invoices View
  if (showInvoiceSearch && customerForInvoices) {
    return (
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            mb: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            borderRadius: 2,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                component="h1"
                gutterBottom
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontWeight: "bold",
                  mb: 1,
                }}
              >
                <ReceiptIcon fontSize={isMobile ? "medium" : "large"} />
                Rechnungen für {getCustomerDisplayName(customerForInvoices)}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Alle Rechnungen dieses Kunden
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                setShowInvoiceSearch(false);
                setCustomerForInvoices(null);
                setCustomerInvoices([]);
              }}
              sx={{
                color: "white",
                borderColor: "white",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              Zurück zu Kunden
            </Button>
          </Stack>
        </Paper>

        {/* Content */}
        {loadingInvoices ? (
          <LoadingSpinner text="Lade Rechnungen..." />
        ) : error ? (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setError(null);
                  loadCustomerInvoices(customerForInvoices);
                }}
              >
                Erneut versuchen
              </Button>
            }
          >
            {error}
          </Alert>
        ) : customerInvoices.length === 0 ? (
          <Paper
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: "center",
              borderRadius: 2,
              background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
            }}
          >
            <ReceiptIcon
              sx={{
                fontSize: { xs: 60, md: 80 },
                color: "text.secondary",
                mb: 2,
              }}
            />
            <Typography
              variant={isMobile ? "h6" : "h5"}
              gutterBottom
              fontWeight="bold"
            >
              Keine Rechnungen gefunden
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Für diesen Kunden wurden noch keine Rechnungen erstellt.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {customerInvoices.map((invoice) => (
              <Grid item xs={12} md={6} lg={4} key={invoice.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  <CardContent sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6" component="h3" fontWeight="bold">
                        Rechnung #{invoice.invoiceNumber}
                      </Typography>
                      {getStatusChip(invoice.status)}
                    </Box>

                    <Stack spacing={1}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          📅 Datum:
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(invoice.createdAt)}
                        </Typography>
                      </Box>

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          💰 Betrag:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {formatPrice(invoice.totalAmount)}
                        </Typography>
                      </Box>

                      {invoice.dueDate && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            📆 Fällig:
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(invoice.dueDate)}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: "bold",
                mb: 1,
              }}
            >
              <PersonIcon fontSize={isMobile ? "medium" : "large"} />
              Kundenverwaltung
            </Typography>
          </Box>

          {!isMobile && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddCustomer}
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                  },
                }}
              >
                Neuer Kunde
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2 }}>
        <Stack spacing={3}>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "primary.main" }}
          >
            Suche & Filter
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Kunden suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  disabled={!searchTerm}
                >
                  Filter löschen
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      {/* Customers Display */}
      {filteredCustomers.length === 0 ? (
        <Paper
          sx={{
            p: { xs: 4, md: 6 },
            textAlign: "center",
            borderRadius: 2,
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          }}
        >
          <PersonIcon
            sx={{
              fontSize: { xs: 60, md: 80 },
              color: "text.secondary",
              mb: 2,
            }}
          />
          <Typography
            variant={isMobile ? "h6" : "h5"}
            gutterBottom
            fontWeight="bold"
          >
            Keine Kunden gefunden
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {searchTerm
              ? "Passen Sie Ihre Suchkriterien an oder erstellen Sie einen neuen Kunden."
              : "Starten Sie mit Ihrem ersten Kunden und verwalten Sie alle Kundendaten professionell."}
          </Typography>
          {!searchTerm && (
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={handleAddCustomer}
              sx={{
                mt: 3,
                borderRadius: 2,
                px: 4,
                py: 1.5,
              }}
            >
              Ersten Kunden anlegen
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredCustomers.map((customer) => (
            <Grid item xs={12} md={6} lg={4} key={customer.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                  },
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: "primary.main",
                          width: 48,
                          height: 48,
                        }}
                      >
                        {getCustomerInitials(customer)}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="h6"
                          component="h3"
                          fontWeight="bold"
                        >
                          {getCustomerDisplayName(customer)}
                        </Typography>
                        {customer.company && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <BusinessIcon fontSize="small" />
                            {customer.company}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {getCustomerStatusChip(customer)}
                  </Box>

                  <Stack spacing={1}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2">{customer.email}</Typography>
                    </Box>

                    {customer.phone && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {customer.phone}
                        </Typography>
                      </Box>
                    )}

                    {customer.address && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {customer.address}
                          {customer.city &&
                            `, ${customer.postalCode} ${customer.city}`}
                        </Typography>
                      </Box>
                    )}

                    {customer.vehicleLicensePlates &&
                      customer.vehicleLicensePlates.length > 0 && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            🚗 Kennzeichen:
                          </Typography>
                          <Typography variant="body2">
                            {customer.vehicleLicensePlates.join(", ")}
                          </Typography>
                        </Box>
                      )}
                  </Stack>
                </CardContent>

                <Divider />

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                    <Button
                      size="small"
                      startIcon={<ReceiptIcon />}
                      onClick={() => loadCustomerInvoices(customer)}
                      sx={{ flex: 1 }}
                    >
                      Rechnungen
                    </Button>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditCustomer(customer)}
                      sx={{ flex: 1 }}
                    >
                      Bearbeiten
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        setCustomerToDelete(customer);
                        setShowDeleteDialog(true);
                      }}
                    >
                      Löschen
                    </Button>
                  </Stack>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Floating Action Button for mobile */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          display: { xs: "flex", md: "none" },
        }}
        onClick={handleAddCustomer}
      >
        <AddIcon />
      </Fab>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Kunde löschen"
        message={`Möchten Sie den Kunden "${customerToDelete?.firstName} ${customerToDelete?.lastName}" wirklich löschen?`}
        confirmText="Löschen"
        cancelText="Abbrechen"
        type="danger"
        onConfirm={handleDeleteCustomer}
        onCancel={() => {
          setShowDeleteDialog(false);
          setCustomerToDelete(null);
        }}
      />
    </Container>
  );
};

// Customer Form Component
interface CustomerFormProps {
  customer: Customer | null;
  onSave: (customer: any) => void;
  onCancel: () => void;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  customer,
  onSave,
  onCancel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [formData, setFormData] = useState({
    firstName: customer?.firstName || "",
    lastName: customer?.lastName || "",
    company: customer?.company || "",
    email: customer?.email || "",
    phone: customer?.phone || "",
    address: customer?.address || "",
    city: customer?.city || "",
    postalCode: customer?.postalCode || "",
    country: customer?.country || "Deutschland",
    drivingLicense: customer?.drivingLicense || "",
    vehicleLicensePlates: customer?.vehicleLicensePlates?.join(", ") || "",
    notes: customer?.notes || "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const customerData = {
      ...formData,
      vehicleLicensePlates: formData.vehicleLicensePlates
        .split(",")
        .map((plate) => plate.trim())
        .filter((plate) => plate.length > 0),
    };

    onSave(customerData);
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: "bold",
                mb: 1,
              }}
            >
              <PersonIcon fontSize={isMobile ? "medium" : "large"} />
              {customer ? "Kunde bearbeiten" : "Neuen Kunden anlegen"}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {customer
                ? "Bearbeiten Sie die Kundendaten"
                : "Erfassen Sie einen neuen Kunden"}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "primary.main", mb: 3 }}
              >
                Grunddaten
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Vorname"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nachname"
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Unternehmen"
                    value={formData.company}
                    onChange={(e) =>
                      setFormData({ ...formData, company: e.target.value })
                    }
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="E-Mail"
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Telefon"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Address Information */}
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "primary.main", mb: 3 }}
              >
                Adresse
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Adresse"
                    value={formData.address}
                    onChange={(e) =>
                      setFormData({ ...formData, address: e.target.value })
                    }
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="PLZ"
                    value={formData.postalCode}
                    onChange={(e) =>
                      setFormData({ ...formData, postalCode: e.target.value })
                    }
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Stadt"
                    value={formData.city}
                    onChange={(e) =>
                      setFormData({ ...formData, city: e.target.value })
                    }
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Land"
                    value={formData.country}
                    onChange={(e) =>
                      setFormData({ ...formData, country: e.target.value })
                    }
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Additional Information */}
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "primary.main", mb: 3 }}
              >
                Zusätzliche Informationen
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Führerschein"
                    value={formData.drivingLicense}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        drivingLicense: e.target.value,
                      })
                    }
                    placeholder="z.B. B, BE, C"
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Kennzeichen (kommagetrennt)"
                    value={formData.vehicleLicensePlates}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        vehicleLicensePlates: e.target.value,
                      })
                    }
                    placeholder="z.B. B-MW 1234, M-KFZ 5678"
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notizen"
                    multiline
                    rows={3}
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Form Actions */}
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="flex-end"
              >
                <Button
                  type="button"
                  variant="outlined"
                  onClick={onCancel}
                  sx={{ minWidth: 120 }}
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ minWidth: 120 }}
                >
                  {customer ? "Speichern" : "Erstellen"}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};
export default CustomersPage;
